// src/models/settingsConfig/modelConfigType.ts
import { z } from "zod";
var modelConfigType = z.object({
  selected: z.boolean().optional(),
  renameTo: z.string().optional(),
  importFrom: z.string().optional(),
  exportPath: z.string().optional()
});
var modelsConfigType = z.record(z.string(), modelConfigType);

// src/models/settingsConfig/componentsConfigType.ts
import { z as z2 } from "zod";
var componentsConfigType = z2.object({
  models: modelsConfigType
});

// src/models/settingsConfig/operationConfigType.ts
import { z as z3 } from "zod";
var rtkQueryConfigType = z3.object({
  selected: z3.boolean().optional()
});
var muiFormConfigType = z3.object({
  selected: z3.boolean().optional(),
  exportPath: z3.string().optional()
});
var operationConfigType = z3.object({
  "rtk-query": rtkQueryConfigType.optional(),
  "mui-form": muiFormConfigType.optional()
});

// src/models/method/types.ts
import { z as z4 } from "zod";
var methodValues = [
  "get",
  "put",
  "post",
  "delete",
  "options",
  "head",
  "patch",
  "trace"
];
var methodValuesNoTrace = [
  "get",
  "put",
  "post",
  "delete",
  "options",
  "head",
  "patch"
];
var method = z4.enum([
  "get",
  "put",
  "post",
  "delete",
  "options",
  "head",
  "patch",
  "trace"
]);
var methodNoTrace = z4.enum([
  "get",
  "put",
  "post",
  "delete",
  "options",
  "head",
  "patch"
]);
var methods = z4.array(method);

// src/models/settingsConfig/methodsConfigType.ts
import { z as z5 } from "zod";
var methodsConfigType = z5.record(method, operationConfigType);

// src/models/settingsConfig/types.ts
import { z as z6 } from "zod";
var defaultExportPath = "./src/index.ts";
var rtkQueryRootConfigType = z6.object({
  baseApiImport: z6.string()
});
var settingsConfigType = z6.object({
  schemaHash: z6.string(),
  exportPath: z6.string().optional(),
  operations: z6.record(z6.string(), methodsConfigType),
  components: componentsConfigType,
  transformers: z6.object({
    "rtk-query": rtkQueryRootConfigType.optional()
  })
});

// src/types/fileArgs.ts
import { z as z7 } from "zod";
var fileArgs = z7.object({
  directory: z7.string(),
  exportPath: z7.string(),
  destination: z7.string()
});

// src/types/parseRequestBody.ts
import { z as z8 } from "zod";
var parseRequestBody = z8.object({
  document: z8.string()
});

// src/types/pathsConfigType.ts
import { z as z9 } from "zod";
var pathsConfigType = z9.record(z9.string(), methodsConfigType);

// src/types/GenerateContext.ts
var isRef = (arg) => {
  return arg.schematicType === "ref";
};

// src/engine/discriminator/types.ts
import { z as z10 } from "zod";
var oasDiscriminator = z10.object({
  schematicType: z10.literal("discriminator"),
  propertyName: z10.string(),
  mapping: z10.record(z10.string()).optional()
});

// src/engine/markdown/types.ts
import { z as z11 } from "zod";
var markdown = z11.string();

// src/engine/example/types.ts
import { z as z12 } from "zod";
var oasExample = z12.object({
  schematicType: z12.literal("example"),
  summary: z12.string().optional(),
  description: markdown.optional(),
  value: z12.any().optional()
  // externalValue: url.optional()
});

// src/engine/ref/types.ts
import { z as z13 } from "zod";
var oasSchemaRef = z13.object({
  schematicType: z13.literal("ref"),
  refType: z13.enum(["schema"]),
  $ref: z13.string(),
  summary: z13.string().optional(),
  description: markdown.optional()
});
var oasResponseRef = z13.object({
  schematicType: z13.literal("ref"),
  refType: z13.enum(["response"]),
  $ref: z13.string(),
  summary: z13.string().optional(),
  description: markdown.optional()
});
var oasParameterRef = z13.object({
  schematicType: z13.literal("ref"),
  refType: z13.enum(["parameter"]),
  $ref: z13.string(),
  summary: z13.string().optional(),
  description: markdown.optional()
});
var oasExampleRef = z13.object({
  schematicType: z13.literal("ref"),
  refType: z13.enum(["example"]),
  $ref: z13.string(),
  summary: z13.string().optional(),
  description: markdown.optional()
});
var oasRequestBodyRef = z13.object({
  schematicType: z13.literal("ref"),
  refType: z13.enum(["requestBody"]),
  $ref: z13.string(),
  summary: z13.string().optional(),
  description: markdown.optional()
});
var oasHeaderRef = z13.object({
  schematicType: z13.literal("ref"),
  refType: z13.enum(["header"]),
  $ref: z13.string(),
  summary: z13.string().optional(),
  description: markdown.optional()
});
var oasPathItemRef = z13.object({
  schematicType: z13.literal("ref"),
  refType: z13.enum(["pathItem"]),
  $ref: z13.string(),
  summary: z13.string().optional(),
  description: markdown.optional()
});
var oasRef = z13.union([
  oasSchemaRef,
  oasResponseRef,
  oasParameterRef,
  oasExampleRef,
  oasRequestBodyRef,
  oasHeaderRef,
  oasPathItemRef
]);

// src/engine/schemaValues.ts
import { z as z14 } from "zod";
var oasArray = z14.object({
  schematicType: z14.literal("schema"),
  // Add soon
  type: z14.literal("array"),
  // additionalItems: z.lazy(() => z.union([z.boolean(), jsonSchema4]).optional()),
  items: z14.lazy(() => z14.union([oasSchema, oasSchemaRef])),
  title: z14.string().optional(),
  description: z14.string().optional(),
  default: z14.array(z14.any()).optional()
  // Add soon
  // maxItems: z.number().optional(),
  // minItems: z.number().optional(),
  // uniqueItems: z.boolean().optional()
});
var oasBoolean = z14.object({
  schematicType: z14.literal("schema"),
  title: z14.string().optional(),
  description: z14.string().optional(),
  default: z14.boolean().optional(),
  type: z14.literal("boolean")
});
var oasNull = z14.object({
  schematicType: z14.literal("schema"),
  title: z14.string().optional(),
  description: z14.string().optional(),
  default: z14.null().optional(),
  type: z14.literal("null")
});
var oasInteger = z14.object({
  schematicType: z14.literal("schema"),
  title: z14.string().optional(),
  description: z14.string().optional(),
  default: z14.number().int().optional(),
  type: z14.literal("integer")
  // Add soon
  // multipleOf: z.number().optional(),
  // maximum: z.number().optional(),
  // exclusiveMaximum: z.boolean().optional(),
  // minimum: z.number().optional(),
  // exclusiveMinimum: z.boolean().optional()
});
var oasNumber = z14.object({
  schematicType: z14.literal("schema"),
  title: z14.string().optional(),
  description: z14.string().optional(),
  default: z14.number().optional(),
  type: z14.literal("number")
  // Add soon
  // multipleOf: z.number().optional(),
  // maximum: z.number().optional(),
  // exclusiveMaximum: z.boolean().optional(),
  // minimum: z.number().optional(),
  // exclusiveMinimum: z.boolean().optional()
});
var oasString = z14.object({
  schematicType: z14.literal("schema"),
  title: z14.string().optional(),
  description: z14.string().optional(),
  default: z14.string().optional(),
  type: z14.literal("string"),
  // Add soon
  // maxLength: z.number().optional(),
  // minLength: z.number().optional(),
  pattern: z14.string().optional(),
  enums: z14.array(z14.string()).optional(),
  format: z14.enum([
    "date-time",
    "time",
    "date",
    "duration",
    "email",
    "hostname",
    "ipv4",
    "ipv6",
    "uuid",
    "uri",
    "regex",
    "binary"
  ]).optional()
});
var oasObject = z14.object({
  schematicType: z14.literal("schema"),
  type: z14.literal("object"),
  title: z14.string().optional(),
  description: z14.string().optional(),
  default: z14.object({}).optional(),
  // Add soon
  // maxProperties: z.number().optional(),
  // Add soon
  // minProperties: z.number().optional(),
  discriminator: oasDiscriminator.optional(),
  properties: z14.lazy(() => {
    return z14.record(z14.union([oasSchema, oasSchemaRef])).optional();
  }),
  // Add soon
  // patternProperties: z.lazy(() => z.record(jsonSchema4).optional()),
  required: z14.array(z14.string()).optional(),
  allOf: z14.lazy(() => z14.array(oasObject).optional()),
  // Use oneOf instead of anyOf
  // anyOf: z.lazy(() => z.array(jsonSchema4).optional()),
  oneOf: z14.lazy(() => z14.array(oasObject).optional()),
  additionalProperties: z14.lazy(() => {
    return z14.union([z14.boolean(), oasSchema, oasSchemaRef, z14.record(z14.never())]).optional();
  })
});
var oasSchema = z14.union([
  oasObject,
  oasArray,
  oasBoolean,
  oasString,
  oasNumber,
  oasNull,
  oasInteger
]);

// src/engine/mediaItem/types.ts
import { z as z15 } from "zod";
var oasMediaTypeItem = z15.object({
  schematicType: z15.literal("mediaType"),
  mediaType: z15.string(),
  schema: z15.union([oasSchema, oasSchemaRef]).optional(),
  // example: z.any().optional(),
  examples: z15.record(z15.union([oasExample, oasExampleRef])).optional()
  // encoding: z.lazy(() => z.record(encoding).optional())
});

// src/engine/header/types.ts
import { z as z16 } from "zod";
var oasHeader = z16.object({
  schematicType: z16.literal("header"),
  description: markdown.optional(),
  required: z16.boolean().optional(),
  deprecated: z16.boolean().optional(),
  allowEmptyValue: z16.boolean().optional(),
  // Default values (based on value of in): for query - form; for path - simple; for header - simple; for cookie - form.
  // style: z.string().optional(),
  // explode: z.boolean().optional(),
  // allowReserved: z.boolean().optional(),
  schema: z16.union([oasSchema, oasSchemaRef]).optional(),
  // example: z.any().optional(),
  examples: z16.record(z16.union([oasExample, oasExampleRef])).optional(),
  content: z16.record(oasMediaTypeItem).optional()
});

// src/engine/parameterLocation/types.ts
import { z as z17 } from "zod";
var oasParameterLocation = z17.enum([
  "query",
  "header",
  "path",
  "cookie"
]);

// src/engine/parameter/types.ts
import { z as z18 } from "zod";
var oasParameter = z18.object({
  schematicType: z18.literal("parameter"),
  name: z18.string(),
  location: oasParameterLocation,
  description: markdown.optional(),
  required: z18.boolean().optional(),
  deprecated: z18.boolean().optional(),
  allowEmptyValue: z18.boolean().optional(),
  // Default values (based on value of in): for query - form; for path - simple; for header - simple; for cookie - form.
  // style: z.string().optional(),
  // explode: z.boolean().optional(),
  // allowReserved: z.boolean().optional(),
  schema: z18.union([oasSchema, oasSchemaRef]).optional(),
  // example: z.any().optional(),
  examples: z18.record(z18.union([oasExample, oasExampleRef])).optional(),
  content: z18.record(oasMediaTypeItem).optional()
});

// src/engine/pathItem/types.ts
import { z as z19 } from "zod";
var oasPathItem = z19.object({
  schematicType: z19.literal("pathItem"),
  $ref: z19.string().optional(),
  summary: z19.string().optional(),
  description: markdown.optional(),
  // servers: z.array(server).optional(),
  parameters: z19.array(z19.union([oasParameter, oasParameterRef])).optional()
});

// src/engine/requestBody/types.ts
import { z as z20 } from "zod";
var oasRequestBody = z20.object({
  schematicType: z20.literal("requestBody"),
  description: markdown.optional(),
  content: z20.record(oasMediaTypeItem),
  required: z20.boolean().optional()
});

// src/engine/response/types.ts
import { z as z21 } from "zod";
var oasResponse = z21.object({
  schematicType: z21.literal("response"),
  description: z21.string(),
  headers: z21.record(z21.union([oasHeader, oasHeaderRef])).optional(),
  content: z21.record(oasMediaTypeItem).optional()
  // links: z.record(z.union([link, ref])).optional()
});

// src/engine/operation/types.ts
import { z as z22 } from "zod";
var oasOperation = z22.object({
  schematicType: z22.literal("operation"),
  pathItem: oasPathItem,
  path: z22.string(),
  method: z22.enum([
    "get",
    "put",
    "post",
    "delete",
    "options",
    "head",
    "patch",
    "trace"
  ]),
  tags: z22.array(z22.string()).optional(),
  summary: z22.string().optional(),
  description: markdown.optional(),
  // externalDocs: externalDocs.optional(),
  // operationId: z.string().optional(),
  parameters: z22.array(z22.union([oasParameter, oasParameterRef])).optional(),
  requestBody: z22.union([oasRequestBody, oasRequestBodyRef]).optional(),
  responses: z22.record(z22.union([oasResponse, oasResponseRef])),
  // callbacks: z.never(),
  deprecated: z22.boolean().optional()
  // security: z.never(),
  // servers: z.array(server).optional()
});

// src/engine/url/types.ts
import { z as z23 } from "zod";
var url = z23.string();

// src/engine/oasTypes.ts
import { z as z24 } from "zod";
var openapi = z24.string();
var oasContact = z24.object({
  schematicType: z24.literal("contact"),
  name: z24.string().optional(),
  url: url.optional(),
  email: z24.string().email().optional()
});
var oasLicense = z24.object({
  schematicType: z24.literal("license"),
  name: z24.string(),
  url: url.optional(),
  identifier: z24.string().optional()
});
var oasInfo = z24.object({
  schematicType: z24.literal("info"),
  title: z24.string(),
  summary: z24.string().optional(),
  description: markdown.optional(),
  termsOfService: url.optional(),
  contact: oasContact.optional(),
  license: oasLicense.optional(),
  version: z24.string()
});
var oasComponents = z24.object({
  schematicType: z24.literal("components"),
  models: z24.record(z24.union([oasSchema, oasSchemaRef])).optional(),
  responses: z24.record(z24.union([oasResponse, oasResponseRef])).optional(),
  parameters: z24.record(z24.union([oasParameter, oasParameterRef])).optional(),
  examples: z24.record(z24.union([oasExample, oasExampleRef])).optional(),
  requestBodies: z24.record(z24.union([oasRequestBody, oasRequestBodyRef])).optional(),
  headers: z24.record(z24.union([oasHeader, oasHeaderRef])).optional(),
  // securitySchemes: z.record(z.union([securityScheme, ref])),
  // links: z.record(z.union([link, ref])),
  // callbacks: z.never(),
  pathItems: z24.record(z24.union([oasPathItem, oasPathItemRef])).optional()
});
var oasComponentType = z24.union([
  oasSchema,
  oasResponse,
  oasParameter,
  oasExample,
  oasRequestBody,
  oasHeader,
  oasPathItem
]);
var oasTag = z24.object({
  schematicType: z24.literal("tag"),
  name: z24.string(),
  description: markdown.optional()
  // externalDocs: externalDocs.optional()
});
var oasRoot = z24.object({
  schematicType: z24.literal("openapi"),
  openapi,
  info: oasInfo,
  jsonSchemaDialect: url.optional(),
  // servers: z.array(server).optional(),
  operations: z24.array(oasOperation),
  // webhooks: z.record(z.union([pathItem, ref])).optional(),
  components: oasComponents.optional(),
  // security: z.never(),
  tags: z24.array(oasTag).optional()
  // externalDocs: externalDocs.optional()
});
var notImplemented = z24.object({
  schematicType: z24.literal("notImplemented"),
  message: z24.string().optional()
});
var flows = z24.object({
  implicit: z24.object({
    authorizationUrl: url,
    refreshUrl: url.optional(),
    scopes: z24.record(z24.string())
  }).optional(),
  password: z24.object({
    tokenUrl: url,
    refreshUrl: url.optional(),
    scopes: z24.record(z24.string())
  }).optional(),
  clientCredentials: z24.object({
    tokenUrl: url,
    refreshUrl: url.optional(),
    scopes: z24.record(z24.string())
  }).optional(),
  authorizationCode: z24.object({
    authorizationUrl: url,
    tokenUrl: url,
    refreshUrl: url.optional(),
    scopes: z24.record(z24.string())
  }).optional()
});
var securityScheme = z24.object({
  description: markdown.optional()
}).and(
  z24.discriminatedUnion("type", [
    z24.object({
      type: z24.literal("apiKey"),
      name: z24.string().optional(),
      in: z24.enum(["query", "header", "cookie"]).optional()
    }),
    z24.object({
      type: z24.literal("http"),
      scheme: z24.string(),
      bearerFormat: z24.string().optional()
    }),
    z24.object({
      type: z24.literal("oauth2"),
      flows
    }),
    z24.object({
      type: z24.literal("openIdConnect"),
      openIdConnectUrl: url
    }),
    z24.object({
      type: z24.literal("mutualTLS")
    })
  ])
);
var externalDocs = z24.object({
  url,
  description: markdown.optional()
});
var xml = z24.object({
  name: z24.string().optional(),
  namespace: z24.string().optional(),
  prefix: z24.string().optional(),
  attribute: z24.boolean().optional(),
  wrapped: z24.boolean().optional()
});
var serverVariable = z24.object({
  default: z24.string(),
  description: markdown.optional(),
  enum: z24.array(z24.string()).nonempty().optional()
});
var server = z24.object({
  url,
  description: markdown.optional(),
  variables: z24.record(serverVariable).optional()
});
var link = z24.object({
  operationRef: url.optional(),
  operationId: z24.string().optional(),
  parameters: z24.record(z24.union([oasParameter, oasParameterRef])).optional(),
  requestBody: z24.union([z24.any(), oasRequestBodyRef]).optional(),
  description: markdown.optional()
  // server: server.optional()
});
var encoding = z24.object({
  contentType: z24.string().optional(),
  headers: z24.lazy(() => z24.record(z24.union([oasHeader, oasHeaderRef])).optional())
  // style: z.string().optional(),
  // explode: z.boolean().optional(),
  // allowReserved: z.boolean().optional()
});

// src/engine/operation/mocks.ts
var mockOasOperation = {
  schematicType: "operation",
  pathItem: {
    schematicType: "pathItem"
  },
  path: "/pet",
  method: "put",
  tags: ["pet"],
  summary: "Update an existing pet",
  description: "Update an existing pet by Id",
  requestBody: {
    schematicType: "requestBody",
    description: "Update an existent pet in the store",
    content: {
      "application/json": {
        schematicType: "mediaType",
        mediaType: "application/json",
        schema: {
          schematicType: "ref",
          refType: "schema",
          $ref: "#/components/schemas/Pet"
        }
      },
      "application/xml": {
        schematicType: "mediaType",
        mediaType: "application/xml",
        schema: {
          schematicType: "ref",
          refType: "schema",
          $ref: "#/components/schemas/Pet"
        }
      },
      "application/x-www-form-urlencoded": {
        schematicType: "mediaType",
        mediaType: "application/x-www-form-urlencoded",
        schema: {
          schematicType: "ref",
          refType: "schema",
          $ref: "#/components/schemas/Pet"
        }
      }
    },
    required: true
  },
  responses: {
    "200": {
      schematicType: "response",
      description: "Successful operation",
      content: {
        "application/xml": {
          schematicType: "mediaType",
          mediaType: "application/xml",
          schema: {
            schematicType: "ref",
            refType: "schema",
            $ref: "#/components/schemas/Pet"
          }
        },
        "application/json": {
          schematicType: "mediaType",
          mediaType: "application/json",
          schema: {
            schematicType: "ref",
            refType: "schema",
            $ref: "#/components/schemas/Pet"
          }
        }
      }
    },
    "400": {
      schematicType: "response",
      description: "Invalid ID supplied"
    },
    "404": {
      schematicType: "response",
      description: "Pet not found"
    },
    "405": {
      schematicType: "response",
      description: "Validation exception"
    }
  }
};
var mockOasOperationThree = {
  deprecated: false,
  description: "Retrieve the Metabase link for the analytics dashboard of one of your [Sequence Accounts](/docs/organisations-and-accounts).",
  method: "get",
  parameters: [
    {
      description: "Your [API credentials](/docs/authentication). Eg. `Basic {credentials}`.",
      location: "header",
      name: "Authorization",
      required: true,
      schema: {
        schematicType: "schema",
        type: "string"
      },
      schematicType: "parameter"
    }
  ],
  path: "/analytics-dashboard-link",
  pathItem: {
    schematicType: "pathItem"
  },
  responses: {
    "200": {
      content: {
        "application/json": {
          examples: {
            "application/json": {
              schematicType: "example",
              value: {
                metabaseDashboardLink: "https://sandbox.sequencehq.com/analytics/dashboard1"
              }
            }
          },
          mediaType: "application/json",
          schema: {
            $ref: "#/components/schemas/MetabaseDashboardLinkResponse",
            refType: "schema",
            schematicType: "ref"
          },
          schematicType: "mediaType"
        }
      },
      description: "OK",
      schematicType: "response"
    },
    "500": {
      content: {},
      description: "",
      schematicType: "response"
    }
  },
  schematicType: "operation",
  summary: "Get Metabase dashboard link",
  tags: []
};
var mockOasOperationWithParams = {
  schematicType: "operation",
  pathItem: {
    schematicType: "pathItem"
  },
  path: "/oauth-clients",
  method: "get",
  tags: [],
  summary: "List OAuth Clients",
  description: "List all OAuth clients for Sequence account",
  parameters: [
    {
      schematicType: "parameter",
      name: "limit",
      location: "query",
      description: "Maximum number of objects to return per-page.",
      required: false,
      schema: {
        schematicType: "schema",
        type: "integer"
      }
    },
    {
      schematicType: "parameter",
      name: "after",
      location: "query",
      description: "Pagination offset. To page through items, omit this parameter to retrieve the first page, and then successively use the value you get from `pagination.after` or `pagination.before` to retrieve each page.",
      required: false,
      schema: {
        schematicType: "schema",
        type: "string"
      }
    },
    {
      schematicType: "parameter",
      name: "before",
      location: "query",
      description: "Pagination offset. To page through items, omit this parameter to retrieve the first page, and then successively use the value you get from `pagination.next` or `pagination.previous` to retrieve each page.",
      required: false,
      schema: {
        schematicType: "schema",
        type: "string"
      }
    },
    {
      schematicType: "parameter",
      name: "Authorization",
      location: "header",
      description: "Your [API credentials](/docs/authentication). Eg. `Basic {credentials}`.",
      required: true,
      schema: {
        schematicType: "schema",
        type: "string"
      }
    }
  ],
  responses: {
    "200": {
      schematicType: "response",
      description: "OK",
      content: {
        "application/json": {
          schematicType: "mediaType",
          mediaType: "application/json",
          schema: {
            schematicType: "ref",
            refType: "schema",
            $ref: "#/components/schemas/ListOAuthClientsEndpointResponseModel"
          },
          examples: {
            "application/json": {
              schematicType: "example",
              value: {
                items: [
                  {
                    id: "7b1f28b9-a4ad-450b-a0ea-fd647d5109b2",
                    sequenceAccountId: "c9e249d8-5ac9-49af-b9a3-0da9f0946c3d",
                    name: "Client",
                    scopes: ["product"],
                    pinnedApiVersion: "2024-01-01"
                  }
                ],
                pagination: {
                  after: "ZTMwOWU5NDgtMDg4ZS00ZDc3LWI2NTQtY2Q4NTQ5OGYxNTU1IzE2NTgyNjA4NTYwMzMwMTMjREVTQw==",
                  before: "NjRlZjJmZjktMmJjYi00M2RiLWI0ZDUtOTAxMDU4MjRiMTdmIzE2NTgyNjA3NTk3NTE2ODEjQVND",
                  totalResultSize: 100
                }
              }
            }
          }
        }
      }
    },
    "500": {
      schematicType: "response",
      description: "",
      content: {}
    }
  },
  deprecated: false
};

// src/engine/response/mocks.ts
var mockOasResponse = {
  schematicType: "response",
  description: "successful operation",
  content: {
    "application/xml": {
      schematicType: "mediaType",
      mediaType: "application/xml",
      schema: {
        schematicType: "schema",
        type: "array",
        items: {
          schematicType: "ref",
          refType: "schema",
          $ref: "#/components/schemas/Pet"
        }
      }
    },
    "application/json": {
      schematicType: "mediaType",
      mediaType: "application/json",
      schema: {
        schematicType: "schema",
        type: "array",
        items: {
          schematicType: "ref",
          refType: "schema",
          $ref: "#/components/schemas/Pet"
        }
      }
    }
  }
};

// src/engine/void/types.ts
import { z as z25 } from "zod";
var oasVoid = z25.object({
  schematicType: z25.literal("schema"),
  description: z25.string().optional(),
  type: z25.literal("void")
});

// src/models/schema/types.ts
import { z as z26 } from "zod";
var githubSchema = z26.object({
  sourceType: z26.enum(["github"]),
  repoOwner: z26.string(),
  repoName: z26.string(),
  path: z26.string()
});
var textSchema = z26.object({
  sourceType: z26.enum(["text"])
});
var urlSchema = z26.object({
  sourceType: z26.enum(["url"]),
  url: z26.string()
});
var meta = z26.discriminatedUnion("sourceType", [
  githubSchema,
  textSchema,
  urlSchema
]);
var schemaFormat = z26.enum(["json", "yaml"]);
var schemaModel = z26.object({
  id: z26.string(),
  name: z26.string(),
  content: z26.string(),
  schemaSource: z26.enum(["github", "text", "url"]),
  schemaFormat,
  meta,
  hash: z26.string(),
  ownerId: z26.string(),
  createdAt: z26.string(),
  updatedAt: z26.string()
});
var schemaModels = z26.array(schemaModel);
var schemaInitialModel = meta.and(z26.object({ content: z26.string() }));

// src/models/schema/transformers.ts
import { match } from "ts-pattern";
import { z as z27 } from "zod";
var toSourceFormValues = ({
  meta: meta2,
  content
}) => {
  return match(meta2).with({ sourceType: "github" }, (matched) => ({
    ...matched,
    repo: `${matched.repoOwner}/${matched.repoName}`,
    path: matched.path.split("/").map((name) => ({ name, type: "unknown" })),
    content
  })).with({ sourceType: "text" }, (matched) => ({
    ...matched,
    content
  })).with({ sourceType: "url" }, (matched) => ({
    ...matched,
    content
  })).exhaustive();
};
var sourceFormValues = z27.union([
  z27.object({
    sourceType: z27.literal("github"),
    repo: z27.string(),
    path: z27.array(
      z27.object({ name: z27.string(), type: z27.enum(["unknown", "dir", "file"]) })
    ),
    content: z27.string()
  }),
  z27.object({
    sourceType: z27.literal("url"),
    url: z27.string(),
    content: z27.string()
  }),
  z27.object({
    sourceType: z27.literal("text"),
    content: z27.string()
  }),
  z27.object({
    sourceType: z27.null(),
    content: z27.literal("")
  })
]);

// src/models/transformer/types.ts
import { z as z28 } from "zod";
var transformerModel = z28.object({
  id: z28.string(),
  name: z28.string(),
  description: z28.string().optional(),
  content: z28.record(z28.string()),
  ownerId: z28.string(),
  createdAt: z28.string()
});
var transformerInitialModel = z28.object({
  name: z28.string().min(1, { message: "Name is required" }),
  description: z28.string().optional(),
  content: z28.record(z28.string())
});
var transformerModels = z28.array(transformerModel);

// src/models/github.ts
import { z as z29 } from "zod";
var githubRepoSummary = z29.object({
  id: z29.number().min(1, { message: "id is required" }),
  name: z29.string().min(1, { message: "name is required" }),
  owner: z29.string().min(1, { message: "owner is required" })
});
var githubRepoSummaries = z29.array(githubRepoSummary);

// src/models/settingsConfig/values.ts
var createInitialSettingsConfig = (schemaHash, exportPath) => ({
  schemaHash,
  exportPath,
  operations: {},
  components: {
    models: {}
  },
  transformers: {
    "rtk-query": {
      baseApiImport: 'import { baseApi as api } from "./src/features/api"'
    }
  }
});

// src/models/generateConfig/types.ts
import { z as z30 } from "zod";
var generateLibraryType = z30.enum(["rtk-query", "mui-form"]);
var generateConfigType = z30.object({
  language: z30.enum(["typescript"]),
  library: z30.array(generateLibraryType),
  typeSystem: z30.enum(["zod", "typescript"])
});

// src/models/generateConfig/values.ts
var defaultGenerateConfig = {
  language: "typescript",
  library: ["rtk-query"],
  typeSystem: "zod"
};

// src/models/exportConfig/types.ts
import { z as z31 } from "zod";
var exportType = z31.enum(["github", "zip"]);
var exportConfigType = z31.object({
  type: exportType.nullable(),
  repo: z31.string().optional(),
  title: z31.string().optional(),
  head: z31.string().optional(),
  base: z31.string().optional()
});

// src/models/prettierConfig/types.ts
import { z as z32 } from "zod";
var prettierConfigType = z32.object({
  printWidth: z32.number().optional(),
  tabWidth: z32.number().optional(),
  useTabs: z32.boolean().optional(),
  semi: z32.boolean().optional(),
  singleQuote: z32.boolean().optional(),
  quoteProps: z32.union([
    z32.literal("as-needed"),
    z32.literal("consistent"),
    z32.literal("preserve")
  ]).optional(),
  jsxSingleQuote: z32.boolean().optional(),
  trailingComma: z32.union([z32.literal("none"), z32.literal("es5"), z32.literal("all")]).optional(),
  bracketSameLine: z32.boolean().optional(),
  bracketSpacing: z32.boolean().optional(),
  jsxBracketSameLine: z32.boolean().optional(),
  arrowParens: z32.union([z32.literal("avoid"), z32.literal("always")]).optional(),
  rangeStart: z32.number().optional(),
  rangeEnd: z32.number().optional(),
  requirePragma: z32.boolean().optional(),
  insertPragma: z32.boolean().optional(),
  proseWrap: z32.union([z32.literal("always"), z32.literal("never"), z32.literal("preserve")]).optional(),
  htmlWhitespaceSensitivity: z32.union([z32.literal("css"), z32.literal("strict"), z32.literal("ignore")]).optional(),
  vueIndentScriptAndStyle: z32.boolean().optional(),
  endOfLine: z32.union([
    z32.literal("auto"),
    z32.literal("lf"),
    z32.literal("crlf"),
    z32.literal("cr")
  ]).optional(),
  embeddedLanguageFormatting: z32.union([z32.literal("auto"), z32.literal("off")]).optional(),
  singleAttributePerLine: z32.boolean().optional()
});

// src/models/workflow/types.ts
import { z as z33 } from "zod";
var schemaNodeModel = z33.object({
  id: z33.string(),
  type: z33.literal("schema"),
  position: z33.object({ x: z33.number(), y: z33.number() }),
  selected: z33.boolean().optional(),
  data: z33.object({
    schemaId: z33.string().optional()
  })
});
var exportNodeModel = z33.object({
  id: z33.string(),
  type: z33.literal("export"),
  position: z33.object({ x: z33.number(), y: z33.number() }),
  selected: z33.boolean().optional(),
  data: z33.object({
    exportConfig: exportConfigType.optional()
  })
});
var openSchemaNodeModel = z33.object({
  id: z33.string(),
  type: z33.literal("open-schema"),
  position: z33.object({ x: z33.number(), y: z33.number() }),
  selected: z33.boolean().optional(),
  data: z33.object({
    source: sourceFormValues.optional()
  })
});
var generateNodeModel = z33.object({
  id: z33.string(),
  type: z33.literal("generate"),
  position: z33.object({ x: z33.number(), y: z33.number() }),
  selected: z33.boolean().optional(),
  data: z33.object({
    generateConfig: generateConfigType.optional()
  })
});
var artifactsNodeModel = z33.object({
  id: z33.string(),
  type: z33.literal("artifacts"),
  position: z33.object({ x: z33.number(), y: z33.number() }),
  selected: z33.boolean().optional(),
  data: z33.object({})
});
var settingsNodeModel = z33.object({
  id: z33.string(),
  type: z33.literal("settings"),
  position: z33.object({ x: z33.number(), y: z33.number() }),
  selected: z33.boolean().optional(),
  data: z33.object({
    settingsConfig: settingsConfigType.optional()
  })
});
var workflowContent = z33.object({
  nodes: z33.array(
    z33.union([
      exportNodeModel,
      openSchemaNodeModel,
      schemaNodeModel,
      generateNodeModel,
      artifactsNodeModel,
      settingsNodeModel
    ])
  ),
  edges: z33.array(
    z33.object({
      id: z33.string(),
      source: z33.string(),
      target: z33.string(),
      animated: z33.boolean().optional()
    })
  )
});
var workflowInitialModel = z33.object({
  name: z33.string(),
  content: workflowContent
});
var workflowModel = z33.object({
  id: z33.string(),
  name: z33.string(),
  content: workflowContent,
  ownerId: z33.string(),
  createdAt: z33.string()
});
var workflowModels = z33.array(workflowModel);

// src/models/pullRequest/types.ts
import { z as z34 } from "zod";
var pullRequestType = z34.object({
  owner: z34.string(),
  repo: z34.string(),
  title: z34.string(),
  // 'pull request title'
  head: z34.string(),
  // : 'pull-request-branch-name'
  base: z34.string(),
  // : 'main' /* optional: defaults to default branch */
  files: z34.record(z34.string())
});

// src/api/server/generate.ts
import { z as z35 } from "zod";
var generatePayload = z35.object({
  schemaModel: oasRoot,
  settingsConfig: settingsConfigType.optional(),
  prettierConfig: prettierConfigType,
  generateConfig: generateConfigType
});
var artifactsMapModel = z35.record(z35.string());

// src/api/server/parse.ts
import { z as z36 } from "zod";
var parsePayload = z36.object({
  schemaDocument: z36.string(),
  schemaFormat: z36.enum(["json", "yaml"])
});
export {
  artifactsMapModel,
  componentsConfigType,
  createInitialSettingsConfig,
  defaultExportPath,
  defaultGenerateConfig,
  exportConfigType,
  exportType,
  fileArgs,
  generateConfigType,
  generateLibraryType,
  generatePayload,
  githubRepoSummaries,
  githubRepoSummary,
  isRef,
  method,
  methodNoTrace,
  methodValues,
  methodValuesNoTrace,
  methods,
  methodsConfigType,
  mockOasOperation,
  mockOasOperationThree,
  mockOasOperationWithParams,
  mockOasResponse,
  modelConfigType,
  modelsConfigType,
  muiFormConfigType,
  notImplemented,
  oasArray,
  oasBoolean,
  oasComponentType,
  oasComponents,
  oasContact,
  oasDiscriminator,
  oasExample,
  oasExampleRef,
  oasHeader,
  oasHeaderRef,
  oasInfo,
  oasInteger,
  oasLicense,
  oasMediaTypeItem,
  oasNull,
  oasNumber,
  oasObject,
  oasOperation,
  oasParameter,
  oasParameterLocation,
  oasParameterRef,
  oasPathItem,
  oasPathItemRef,
  oasRef,
  oasRequestBody,
  oasRequestBodyRef,
  oasResponse,
  oasResponseRef,
  oasRoot,
  oasSchema,
  oasSchemaRef,
  oasString,
  oasTag,
  oasVoid,
  operationConfigType,
  parsePayload,
  parseRequestBody,
  pathsConfigType,
  prettierConfigType,
  pullRequestType,
  rtkQueryConfigType,
  rtkQueryRootConfigType,
  schemaInitialModel,
  schemaModel,
  schemaModels,
  settingsConfigType,
  sourceFormValues,
  toSourceFormValues,
  transformerInitialModel,
  transformerModel,
  transformerModels,
  url,
  workflowContent,
  workflowInitialModel,
  workflowModel,
  workflowModels
};
//# sourceMappingURL=index.js.map